import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Activity, Heart, Droplet, Calendar, Moon, Scale } from 'lucide-react';
import './ChartsGraphs.css';
import { getTranslation } from '../config/translations';
import { useLanguage } from '../context/LanguageContext';
import { useGlucose } from '../context/GlucoseContext';
import { useUser } from '../context/UserContext';

const ChartsGraphs: React.FC = () => {
  const { language } = useLanguage();
  const { userData } = useUser();
  const { readings, a1cReadings, getAverageGlucose } = useGlucose();

  const t = (key: string) => getTranslation(language, key);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter'>('week');

  // Generate dates for the last 7 days
  const getDateLabels = () => {
    const dates = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dates.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    }
    return dates;
  };

  const dateLabels = getDateLabels();

  // Build glucose data from readings
  const getGlucoseData = () => {
    const data = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayReadings = readings.filter(r => r.date === dateStr);
      
      const fastingReadings = dayReadings.filter(r => r.type === 'fasting');
      const postMealReadings = dayReadings.filter(r => r.type === 'postMeal');
      
      const fastingAvg = fastingReadings.length > 0 
        ? Math.round(fastingReadings.reduce((sum, r) => sum + r.value, 0) / fastingReadings.length)
        : null;
      const postMealAvg = postMealReadings.length > 0
        ? Math.round(postMealReadings.reduce((sum, r) => sum + r.value, 0) / postMealReadings.length)
        : null;
      
      data.push({
        day: dateLabels[6 - i],
        fasting: fastingAvg || 0,
        postMeal: postMealAvg || 0,
        target: 130,
        hasData: fastingAvg !== null || postMealAvg !== null
      });
    }
    return data;
  };

  const glucoseData = getGlucoseData();

  // Generate sleep data from documents
  const getSleepData = () => {
    const savedDocs = localStorage.getItem('myDocuments');
    let docs: any[] = [];
    if (savedDocs) {
      try { docs = JSON.parse(savedDocs); } catch(e) {}
    } else if (userData?.documents) {
      docs = userData.documents;
    }

    const data = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const docDateStr = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      
      const dayDocs = docs.filter(d => d.date === docDateStr && d.healthcareData && d.healthcareData.sleep);
      let sleepVal = 0;
      if (dayDocs.length > 0) {
        sleepVal = dayDocs[0].healthcareData.sleep;
      }
      
      data.push({
        day: dateLabels[6 - i],
        hours: sleepVal,
        target: 8
      });
    }
    return data;
  };

  const sleepData = getSleepData();
  const maxSleep = Math.max(10, ...sleepData.map(d => d.hours));

  // A1C Trend (last 6 readings)
  let a1cData = [...a1cReadings]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(-6)
    .map(r => {
      const d = new Date(r.date);
      // Ensure valid dates don't break the UI
      return {
        month: isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('en-US', { month: 'short' }),
        value: r.value
      };
    });
    
  if (a1cData.length === 0) {
    a1cData = [{ month: 'N/A', value: 0 }];
  }

  const maxGlucose = Math.max(180, ...glucoseData.flatMap(d => [d.fasting, d.postMeal]));
  const maxA1c = Math.max(...a1cData.map(d => d.value));

  return (
    <div className="charts-graphs">
      <div className="charts-header">
        <h1 className="charts-title">{t('charts')}</h1>
        <div className="time-range-selector">
          <button 
            className={`range-btn ${timeRange === 'week' ? 'active' : ''}`}
            onClick={() => setTimeRange('week')}
          >
            Week
          </button>
          <button 
            className={`range-btn ${timeRange === 'month' ? 'active' : ''}`}
            onClick={() => setTimeRange('month')}
          >
            Month
          </button>
          <button 
            className={`range-btn ${timeRange === 'quarter' ? 'active' : ''}`}
            onClick={() => setTimeRange('quarter')}
          >
            Quarter
          </button>
        </div>
      </div>



      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon glucose">
            <Droplet size={24} />
          </div>
          <div className="card-content">
            <span className="card-label">{t('avgGlucose')}</span>
            <span className="card-value">{getAverageGlucose()} mg/dL</span>
            <span className="card-trend positive">
              <TrendingDown size={14} />
              -3% from last week
            </span>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon a1c">
            <Activity size={24} />
          </div>
          <div className="card-content">
            <span className="card-label">{t('latestA1C')}</span>
            <span className="card-value">{a1cReadings.length > 0 ? a1cReadings[0].value.toFixed(1) : '--'}%</span>
            <span className="card-trend positive">
              <TrendingDown size={14} />
              Improved
            </span>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon" style={{ backgroundColor: 'rgba(233, 30, 99, 0.1)', color: '#e91e63' }}>
            <Heart size={24} />
          </div>
          <div className="card-content">
            <span className="card-label">Blood Pressure</span>
            <span className="card-value">120/80 <small>mmHg</small></span>
            <span className="card-trend positive">
              <TrendingDown size={14} />
              Optimal
            </span>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon" style={{ backgroundColor: 'rgba(156, 39, 176, 0.1)', color: '#9c27b0' }}>
            <Moon size={24} />
          </div>
          <div className="card-content">
            <span className="card-label">Sleep Avg</span>
            <span className="card-value">7h 15m</span>
            <span className="card-trend positive">
              <TrendingUp size={14} />
              +20m from last week
            </span>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon" style={{ backgroundColor: 'rgba(255, 152, 0, 0.1)', color: '#ff9800' }}>
            <Scale size={24} />
          </div>
          <div className="card-content">
            <span className="card-label">Weight</span>
            <span className="card-value">164 <small>lbs</small></span>
            <span className="card-trend positive">
              <TrendingDown size={14} />
              -1.5 lbs this month
            </span>
          </div>
        </div>

      </div>

      <div className="charts-grid">
        {/* Blood Glucose Chart */}
        <div className="chart-card large">
          <div className="chart-header">
            <h2 className="chart-title">{t('bloodGlucoseLevels')}</h2>
            <div className="chart-legend">
              <span className="legend-item">
                <span className="legend-dot fasting"></span>
                {t('fasting')}
              </span>
              <span className="legend-item">
                <span className="legend-dot post-meal"></span>
                {t('postMeal')}
              </span>
              <span className="legend-item">
                <span className="legend-dot target"></span>
                {t('target')}
              </span>
            </div>
          </div>
          <div className="chart-container">
            <div className="chart-y-axis">
              <span>180</span>
              <span>150</span>
              <span>120</span>
              <span>90</span>
            </div>
            <div className="chart-content">
              {glucoseData.map((data, index) => (
                <div key={index} className="chart-bar-group">
                  <div className="bar-container">
                    <div 
                      className="chart-bar fasting"
                      style={{ height: `${(data.fasting / maxGlucose) * 100}%` }}
                      title={`Fasting: ${data.fasting} mg/dL`}
                    >
                      <span className="bar-value">{data.fasting}</span>
                    </div>
                    <div 
                      className="chart-bar post-meal"
                      style={{ height: `${(data.postMeal / maxGlucose) * 100}%` }}
                      title={`Post-Meal: ${data.postMeal} mg/dL`}
                    >
                      <span className="bar-value">{data.postMeal}</span>
                    </div>
                    <div 
                      className="target-line"
                      style={{ bottom: `${(data.target / maxGlucose) * 100}%` }}
                    ></div>
                  </div>
                  <span className="chart-label">{data.day}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* A1C Trend Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <h2 className="chart-title">{t('a1cTrend')}</h2>
            <span className="chart-subtitle">{t('lastSixMonths')}</span>
          </div>
          <div className="chart-container">
            <div className="line-chart">
              <div className="line-chart-grid">
                {[8, 7, 6, 5].map((val) => (
                  <div key={val} className="grid-line">
                    <span className="grid-label">{val}%</span>
                  </div>
                ))}
              </div>
              <svg className="line-chart-svg" viewBox="0 0 300 150" preserveAspectRatio="none">
                <polyline
                  points={a1cData.map((d, i) => `${(i / Math.max(1, a1cData.length - 1)) * 300},${150 - ((d.value - 5) / 3) * 150}`).join(' ')}
                  fill="none"
                  stroke="#00bcd4"
                  strokeWidth="3"
                />
                {a1cData.map((d, i) => (
                  <circle
                    key={i}
                    cx={(i / Math.max(1, a1cData.length - 1)) * 300}
                    cy={150 - ((d.value - 5) / 3) * 150}
                    r="4"
                    fill="#00bcd4"
                  />
                ))}
              </svg>
              <div className="line-chart-labels">
                {a1cData.map((d, i) => (
                  <span key={i} className="x-label">{d.month}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sleep Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <h2 className="chart-title">Sleep Records</h2>
            <div className="chart-legend">
              <span className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: '#9c27b0' }}></span>
                Hours
              </span>
              <span className="legend-item">
                <span className="legend-dot target"></span>
                Target (8h)
              </span>
            </div>
          </div>
          <div className="chart-container">
            <div className="chart-y-axis">
              <span>{Math.ceil(maxSleep)}h</span>
              <span>{Math.ceil(maxSleep * 0.75)}h</span>
              <span>{Math.ceil(maxSleep * 0.5)}h</span>
              <span>{Math.ceil(maxSleep * 0.25)}h</span>
            </div>
            <div className="chart-content">
              {sleepData.map((data, index) => (
                <div key={index} className="chart-bar-group">
                  <div className="bar-container">
                    <div 
                      className="chart-bar"
                      style={{ height: `${(data.hours / maxSleep) * 100}%`, backgroundColor: '#9c27b0', opacity: 0.8 }}
                      title={`Sleep: ${data.hours} hours`}
                    >
                      <span className="bar-value">{data.hours || ''}</span>
                    </div>
                    <div 
                      className="target-line"
                      style={{ bottom: `${(data.target / maxSleep) * 100}%` }}
                    ></div>
                  </div>
                  <span className="chart-label">{data.day}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ChartsGraphs;
